import { useContext } from 'react';
import { resolveCalendarZone } from '@/components/list/listDate';
import { ServiceLocationContext } from './locationContextValue';
import { TenantZoneContext } from './tenantZoneContextValue';

/**
 * The tenant's default zone from the app-level `TenantZoneProvider` (DNKADM-0478), or
 * `null` while it loads, on a failed read, or outside the provider.
 */
export function useTenantZone(): string | null {
  return useContext(TenantZoneContext);
}

/**
 * The calendar zone for "today", date windows and moved timestamps (DNKADM-0439,
 * DNKADM-0478): the selected location's IANA zone, else the tenant's (`tenantZone` when a
 * screen passes its own, else the app-level `TenantZoneProvider`'s), else `null`, which
 * every helper in `listDate.ts` reads as the device's zone.
 *
 * Read tolerantly: outside a `LocationProvider`, or while the locations load, there is no
 * location zone yet and the next fallback applies. It issues no request of its own.
 */
export function useCalendarZone(tenantZone: string | null = null): string | null {
  const ctx = useContext(ServiceLocationContext);
  const appTenantZone = useContext(TenantZoneContext);
  const locationZone = ctx?.locations.find((l) => l.id === ctx.selectedId)?.timezone ?? null;
  return resolveCalendarZone(locationZone, tenantZone ?? appTenantZone);
}
